// TextEditor.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import checkIsMobile from '../resource/checkIsMobile.js';
const EDITOR_LINE_HEIGHT = 16;
class TextEditor extends React.PureComponent {
  state = {
    value: this.props.value,
  };

  componentDidUpdate({ value: prevPropsValue }) {
    const { value } = this.state;
    const { value: currentPropsValue } = this.props;
    if (value && prevPropsValue && currentPropsValue === '') {
      this.resize();
      this.setState({ value: '' });
    } else if (value !== prevPropsValue) {
      this.resize();
      this.props.onChange({ value });
    }
  }

  componentDidMount() {
    const { shouldAutoFocus } = this.props;
    if (shouldAutoFocus) {
      this.focus();
    }
    this.resize();
  }

  focus = () => this.textarea.focus();
  blur = () => this.textarea.blur();

  resize = () => {
    if (this.props.mode !== 'chat') {
      return;
    }

    setTimeout(() => {
      if (this.textarea) {
        this.textarea.style.height = 'auto';
        this.textarea.style.height = `${this.textarea.scrollHeight}px`;
      }
    }, 0);
  };

  insertNewline(value) {
    const pos = this.textarea.selectionEnd;
    const newValue = `${value.substr(0, pos)}\n${value.substr(pos)}`;
    return { value: newValue, position: pos };
  }

  handleChange = ({ target }) => {
    if (this.props.isDisabled) {
      return;
    }
    const { maxLength, pushToastr } = this.props;

    let text = target.value?.replace(/[\b]/g, ' ');
    if (maxLength && text?.length > maxLength) {
      pushToastr('caption_word_length_error');
      text = text.slice(0, maxLength);
      return;
    }

    this.resize();
    this.setState({ value: text });
  };

  handleKeyDown = event => {
    const { keyCode, shiftKey } = event;
    this.resize();

    if (this.props.mode !== 'chat') {
      return;
    }

    if (keyCode === 13 && shiftKey) {
      event.preventDefault();
      return this.handleCommand('shift-enter');
    } else if (keyCode === 13) {
      event.preventDefault();
      return this.handleCommand('enter');
    }
  };

  handleCommand = command => {
    const isMobile = checkIsMobile();
    const { value } = this.state;

    if (!isMobile && command === 'enter') {
      this.props.onSubmit({ value });
    } else if (
      (!isMobile && command === 'shift-enter') ||
      command === 'enter'
    ) {
      const { value: newValue, position } = this.insertNewline(value);
      this.setState({ value: newValue }, () => {
        this.textarea.selectionEnd = position + 1;
        this.textarea.scrollTop = this.textarea.scrollTop + EDITOR_LINE_HEIGHT;
      });
    }
  };

  render() {
    const { onChange, value, maxLength, ...rest } = this.props;
    return (
      <StyledTextEditor onClick={this.focus}>
        <TextArea
          {...rest}
          value={this.state.value}
          ref={ref => (this.textarea = ref)}
          onClick={e => e.stopPropagation()}
          onChange={this.handleChange}
          onKeyDown={this.handleKeyDown}
        />
      </StyledTextEditor>
    );
  }
}

TextEditor.propTypes = {
  id: PropTypes.string,
  isDisabled: PropTypes.bool,
  shouldAutoFocus: PropTypes.bool,
  maxLength: PropTypes.number,
  value: PropTypes.string,
  mode: PropTypes.oneOf(['default', 'chat']),
  pushToastr: PropTypes.func,
  onChange: PropTypes.func,
  onSubmit: PropTypes.func,
  onPaste: PropTypes.func,
};

TextEditor.defaultProps = {
  shouldAutoFocus: false,
  value: '',
  maxLength: 0,
  mode: 'default',
  pushToastr: () => null,
  onChange: () => null,
  onSubmit: () => null,
};

const StyledTextEditor = styled.div`
  width: 100%;
  height: 100%;
`;

const TextArea = styled.textarea`
  transform: translateY(3px);
  outline: none;
  border: 0;
  background: none;
  width: 100%;
  height: 100%;
  font-size: 16px;
  line-height: ${EDITOR_LINE_HEIGHT}px;
  padding: 0;
  margin: 0;
  color: ${({ isDisabled }) => (isDisabled ? 'grey' : 'currentColor')};
  resize: none;
  max-height: ${({ mode }) => (mode === 'chat' ? '60px' : 'auto')};
`;

export default TextEditor;
